import AuthFieldErrorMessage from '@/components/Auth/AuthFieldErrorMessage'
import CodeEntryField from '@/components/UI/CodeEntryField/CodeEntryField'
import { isWeb } from '@/constants/environment'
import { OAuthErrorTypeEnum } from '@/features/api/oauth/constants'
import { logger } from '@/features/auth/logger'
import { registerFirebase } from '@/features/firebase/thunks/registerFirebase'
import { useAppDispatch } from '@/hooks/redux'
import { useScreenOrientation, useScreenSize } from '@/hooks/screen'
import { fetchAuthTokens } from '@/redux/reducers/auth'
import { fetchLicence } from '@/redux/reducers/licence'
import { isThunkRejectValue } from '@/redux/utils'
import { getAuthProfilePinErrorTranslation } from '@/utils/auth/translation'
import { router } from 'expo-router'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, StyleSheet, View } from 'react-native'

const codeLength = 4

const AuthProfilePinCodeField: FC = () => {
    const screenSize = useScreenSize()
    const screenOrientation = useScreenOrientation()

    const dispatch = useAppDispatch()

    const [errorMessage, setErrorMessage] = useState<string>()
    const [resetSignal, setResetSignal] = useState(0)

    const { t } = useTranslation()

    const handleSubmitEditing = async (pin: string) => {
        if (pin.length < codeLength) return

        try {
            await dispatch(fetchAuthTokens(pin)).unwrap()
            await dispatch(fetchLicence()).unwrap()
            await dispatch(registerFirebase()).unwrap()
        } catch (e) {
            let errorMessage: string

            if (isThunkRejectValue(e)) {
                const { title, message } = getAuthProfilePinErrorTranslation(e)

                errorMessage = t(message)

                if (e.expectedApiError?.identifier === OAuthErrorTypeEnum.InvalidClient) {
                    if (isWeb) return alert(errorMessage)

                    return Alert.alert(t(title!), errorMessage)
                }
            } else {
                logger.errorOnly(e)

                errorMessage = t('unexpectedError')
            }

            setErrorMessage(errorMessage)
            setResetSignal((prev) => prev + 1)

            return
        }

        router.replace('/orders')
    }

    const marginTop = screenSize === 'small' && screenOrientation === 'landscape' ? 8 : 32

    return (
        <View style={[styles.container, { marginTop }]}>
            <CodeEntryField
                key={resetSignal}
                length={codeLength}
                onSubmitEditing={handleSubmitEditing}
            />
            {errorMessage && <AuthFieldErrorMessage>{errorMessage}</AuthFieldErrorMessage>}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        gap: 16,
    },
})

export default AuthProfilePinCodeField
